import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextAWS';

function Account() {
  const { user, logout, updateProfile, updateUserEmail, confirmEmail, changePassword } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailConfirmPending, setEmailConfirmPending] = useState(false);

  useEffect(() => {
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
  }, [user?.firstName, user?.lastName]);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState({ name: false, email: false, password: false });

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (e) {
      setError(e?.message || 'Logout failed');
    }
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading((s) => ({ ...s, name: true }));
    try {
      await updateProfile({
        given_name: firstName.trim() || undefined,
        family_name: lastName.trim() || undefined
      });
      setSuccess('Name updated.');
    } catch (err) {
      setError(err?.message || 'Failed to update name.');
    } finally {
      setLoading((s) => ({ ...s, name: false }));
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!newEmail.trim()) return;
    setLoading((s) => ({ ...s, email: true }));
    try {
      const res = await updateUserEmail(newEmail.trim());
      const step = res?.nextStep?.updateAttributeStep;
      if (step === 'CONFIRM_ATTRIBUTE_WITH_CODE') {
        setEmailConfirmPending(true);
        setSuccess('Check your new email for a verification code.');
      } else {
        setSuccess('Email updated.');
        setNewEmail('');
      }
    } catch (err) {
      setError(err?.message || 'Failed to update email.');
    } finally {
      setLoading((s) => ({ ...s, email: false }));
    }
  };

  const handleConfirmEmailCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!emailCode.trim()) return;
    setLoading((s) => ({ ...s, email: true }));
    try {
      await confirmEmail(emailCode.trim());
      setEmailConfirmPending(false);
      setEmailCode('');
      setSuccess('Email verified and updated.');
    } catch (err) {
      setError(err?.message || 'Invalid verification code.');
    } finally {
      setLoading((s) => ({ ...s, email: false }));
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading((s) => ({ ...s, password: true }));
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess('Password updated.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err?.message || 'Failed to change password.');
    } finally {
      setLoading((s) => ({ ...s, password: false }));
    }
  };

  return (
    <div style={{ maxWidth: '520px', margin: '32px auto 48px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Link to="/" className="btn-text" style={{ fontSize: '14px' }}>← Back to My Leagues</Link>
      </div>

      <h1 className="text-title" style={{ marginBottom: '24px' }}>Account</h1>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Name */}
      <div className="card">
        <h2 className="text-player-name" style={{ marginBottom: '16px' }}>Name</h2>
        <form onSubmit={handleUpdateName}>
          <div className="form-row" style={{ marginBottom: '12px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="firstName">First name</label>
              <input
                id="firstName"
                className="form-control"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="lastName">Last name</label>
              <input
                id="lastName"
                className="form-control"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={loading.name}>
            {loading.name ? 'Saving…' : 'Save name'}
          </button>
        </form>
      </div>

      {/* Email */}
      <div className="card">
        <h2 className="text-player-name" style={{ marginBottom: '8px' }}>Email</h2>
        <p className="text-secondary" style={{ marginBottom: '12px' }}>
          Current: <strong style={{ color: 'var(--foreground)' }}>{user?.email ?? '—'}</strong>
        </p>
        {!emailConfirmPending ? (
          <form onSubmit={handleUpdateEmail}>
            <div className="form-group">
              <label htmlFor="newEmail">New email</label>
              <input
                id="newEmail"
                type="email"
                className="form-control"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="New email address"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-sm" disabled={loading.email}>
              {loading.email ? 'Updating…' : 'Update email'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleConfirmEmailCode}>
            <div className="form-group">
              <label htmlFor="emailCode">Verification code</label>
              <input
                id="emailCode"
                className="form-control"
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value)}
                placeholder="Enter code from email"
              />
            </div>
            <div className="card-actions">
              <button type="submit" className="btn btn-primary btn-sm" disabled={loading.email}>
                {loading.email ? 'Verifying…' : 'Verify'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => { setEmailConfirmPending(false); setEmailCode(''); setSuccess(''); }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Password */}
      <div className="card">
        <h2 className="text-player-name" style={{ marginBottom: '16px' }}>Password</h2>
        <form onSubmit={handleChangePassword}>
          <div className="form-group">
            <label htmlFor="currentPassword">Current password</label>
            <input
              id="currentPassword"
              type="password"
              className="form-control"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Current password"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="newPassword">New password</label>
            <input
              id="newPassword"
              type="password"
              className="form-control"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              required
              minLength={8}
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm new password</label>
            <input
              id="confirmPassword"
              type="password"
              className="form-control"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
            />
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={loading.password}>
            {loading.password ? 'Updating…' : 'Change password'}
          </button>
        </form>
      </div>

      {/* Logout */}
      <div className="card">
        <button
          type="button"
          className="btn btn-danger"
          onClick={handleLogout}
        >
          Log out
        </button>
      </div>
    </div>
  );
}

export default Account;
